'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { formatCurrency } from '@/lib/utils';
import type { ProductOptionGroup, ProductAdditional } from '@/lib/productService';

type FoodSelections = {
  optionGroups: Array<{ groupId: string; optionIds: string[] }>;
  additionals: Array<{ additionalId: string; quantity: number }>;
};

type FoodSelectionsDisplayProps = {
  productId: string;
  selections: FoodSelections;
  locale: string;
};

export function FoodSelectionsDisplay({ productId, selections, locale }: FoodSelectionsDisplayProps) {
  const t = useTranslations('product');
  const [optionGroups, setOptionGroups] = useState<ProductOptionGroup[]>([]);
  const [additionals, setAdditionals] = useState<ProductAdditional[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const response = await fetch(`/api/products/${productId}`);
        if (response.ok) {
          const product = await response.json();
          setOptionGroups(product.optionGroups || []);
          setAdditionals(product.additionals || []);
        }
      } catch (error) {
        console.error('Failed to fetch product data for selections:', error);
      } finally {
        setLoading(false);
      }
    };

    if (productId && selections) {
      fetchProductData();
    } else {
      setLoading(false);
    }
  }, [productId, selections]);

  if (loading || (!selections.optionGroups?.length && !selections.additionals?.length)) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Option Groups */}
      {selections.optionGroups?.map((groupSelection) => {
        const group = optionGroups.find(g => g.id === groupSelection.groupId);
        if (!group || !groupSelection.optionIds.length) return null;

        return (
          <div key={groupSelection.groupId} className="text-sm">
            <span className="font-medium text-muted-foreground">{group.title}:</span>{' '}
            <span className="text-foreground">
              {groupSelection.optionIds
                .map(optionId => {
                  const option = group.options.find(o => o.id === optionId);
                  if (!option) return null;
                  const priceModifier = option.priceModifier;
                  return priceModifier !== 0
                    ? `${option.name} (${priceModifier > 0 ? '+' : ''}${formatCurrency(priceModifier, locale)})`
                    : option.name;
                })
                .filter(Boolean)
                .join(', ')}
            </span>
          </div>
        );
      })}

      {/* Additionals */}
      {selections.additionals?.map((additionalSelection) => {
        const additional = additionals.find(a => a.id === additionalSelection.additionalId);
        if (!additional || additionalSelection.quantity === 0) return null;

        return (
          <div key={additionalSelection.additionalId} className="text-sm">
            <span className="font-medium text-muted-foreground">{t('food.extras')}:</span>{' '}
            <span className="text-foreground">
              {additional.name} x{additionalSelection.quantity}
              {additionalSelection.quantity > 1 && ` (${formatCurrency(additional.price * additionalSelection.quantity, locale)})`}
              {additionalSelection.quantity === 1 && ` (${formatCurrency(additional.price, locale)})`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

